import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../lib/auth';

/**
 * The user guide. Written for the tour team, not for developers: every section
 * matches one screen, with a picture of it and the buttons named as they appear.
 */
type Section = { id: string; title: string; adminOnly?: boolean; body: ReactNode };

function Shot({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="help-shot">
      <img src={src} alt={alt} loading="lazy" />
      <figcaption>{alt}</figcaption>
    </figure>
  );
}

const SECTIONS: Section[] = [
  {
    id: 'signing-in',
    title: 'Signing in',
    body: (
      <>
        <p>
          Open the app and sign in with the username your admin gave you. If you forget your password,
          an admin can reset it under <b>Users</b>; there is no email reset.
        </p>
        <p>Change your own password any time: click your name in the top right, then <b>Account</b>.</p>
        <Shot src="/guide/01-signin.jpg" alt="The sign-in screen" />
        <p>
          What you can do depends on your role. The app hides what your role cannot do, so if a button
          in this guide is missing for you, that is why.
        </p>
        <table className="table plain">
          <thead><tr><th>Role</th><th>Can</th></tr></thead>
          <tbody>
            <tr><td><b>Viewer</b></td><td>Open booklets, read them, download PDFs</td></tr>
            <tr><td><b>Reviewer</b></td><td>The above, plus comment, approve or send back, publish, and share links</td></tr>
            <tr><td><b>Editor</b></td><td>Import files, write text, place photos, copy booklets — but not approve or publish</td></tr>
            <tr><td><b>Admin</b></td><td>Everything, plus Users, Templates and Activity</td></tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'booklets',
    title: 'Your booklets',
    body: (
      <>
        <p>
          The first screen is every booklet the team has, newest first, with its cover and where it stands:
          <b> Being written</b>, <b>Waiting for prices</b>, <b>In review</b>, <b>Needs changes</b>, <b>Approved</b> or <b>Published</b>.
        </p>
        <Shot src="/guide/02-gallery.jpg" alt="Your booklets — every booklet with its cover and status" />
        <p>
          <b>+ New booklet</b> asks for a name and a template. Start it empty, or start from the sample tour if you
          want something to look at while you learn the app.
        </p>
        <p>
          The <b>⋯</b> button on a card holds <b>Copy booklet</b> (a full copy, photos included — handy for next year's
          edition of the same tour), <b>History</b> and <b>Delete</b>.
        </p>
      </>
    ),
  },
  {
    id: 'import',
    title: 'Step 1 — Import files',
    body: (
      <>
        <p>
          A booklet is built from two files: a Word file with the words, and a spreadsheet with the dates,
          distances and prices. You can also type everything by hand and skip this step.
        </p>
        <Shot src="/guide/03-import.jpg" alt="Import files — drop the Word file and the price sheet" />
        <p>
          Do not have the files yet? Use <b>Download the empty Word form</b> and <b>Download the empty Excel form</b>.
          They already have the headings and columns the app reads, so a filled-in form imports with nothing to fix.
          Send them to whoever writes the tour text and whoever keeps the prices.
        </p>
        <p>
          A spreadsheet can also come straight from Google Sheets: paste the link and press <b>Import</b>. The sheet has
          to be shared as “Anyone with the link can view”.
        </p>
        <p>
          After reading a file the app shows what it found. If the columns were not recognised, pick the sheet tab and
          the header row and point each field at the right column, then press <b>Read the sheet again</b>. When the Word
          file and the spreadsheet disagree, you choose which one wins, line by line. Nothing changes until you press
          <b> Use these values</b>.
        </p>
      </>
    ),
  },
  {
    id: 'text',
    title: 'Step 2 — Text',
    body: (
      <>
        <p>
          Nine sections, in the order they appear in the booklet: tour info, route, schedule, day pages, experience,
          packages, what is included, deposits and contact. A tick means the section is complete.
        </p>
        <Shot src="/guide/04-text.jpg" alt="Text — the nine sections, with a tick on the ones that are done" />
        <p>
          Everything saves by itself — the word <i>saved</i> next to the booklet name tells you so. Made a mistake?
          <b> Undo</b> in the top bar, or Ctrl+Z.
        </p>
        <p>
          Prices live under <b>Packages &amp; pricing</b>. Leave a price empty while it is not final: the booklet is marked
          as waiting for prices and cannot be published until it is filled in. The currency (EUR, USD, GBP, CHF, IDR) is
          set once under <b>Tour info</b> and applies to the whole booklet.
        </p>
        <p>If a reviewer left a comment on a section, it shows at the top of that section.</p>
      </>
    ),
  },
  {
    id: 'photos',
    title: 'Step 3 — Photos & pages',
    body: (
      <>
        <p>
          Each photo slot says the size it prints at, for example <i>Best size 1685 × 1302 px · 4:3</i>. Use
          <b> Upload</b> for a new photo or <b>Library</b> to pick one already in this booklet.
        </p>
        <Shot src="/guide/05-photos.jpg" alt="Photos & pages — every slot shows the size it prints at" />
        <p>
          A photo that is much smaller than the slot, or a different shape, still works — the app just warns you that it
          will be cropped or printed soft. Use <b>Focal point</b> to choose what stays in frame and <b>Zoom</b> to come closer.
        </p>
        <p>
          The same screen holds the route map and the page list. The map can be drawn for you from the cities in step 2
          (<b>Generate from cities</b>) or be a screenshot you upload. Under <b>Template &amp; pages</b> you can switch the
          template and untick pages this tour does not need — the cover, the day pages and the closing page always print.
        </p>
      </>
    ),
  },
  {
    id: 'preview',
    title: 'Step 4 — Preview & PDF',
    body: (
      <>
        <p>
          This is the booklet exactly as it will print. Click any text on the page and type to change it. Ctrl+click
          text, or click a photo, to jump to its editor.
        </p>
        <Shot src="/guide/06-preview.jpg" alt="Preview & PDF — the booklet as it prints, with Worth a look above it" />
        <p>
          <b>Worth a look</b> lists what still needs attention: empty photo slots, photos too small for print, and text
          that does not fit. Text that only fits after being shrunk is a warning; text that still overflows is marked on
          the page with <i>Text too long — shorten it</i> and has to be fixed before the booklet can be approved or published.
          Press <b>Show</b> to jump to the page in question.
        </p>
        <p>
          <b>Download PDF</b> gives the PDF for email and WhatsApp. <b>PDF for the printer</b> adds the bleed printers ask
          for and keeps the page count even.
        </p>
      </>
    ),
  },
  {
    id: 'review',
    title: 'Comments and review',
    body: (
      <>
        <p>
          The panel on the right of the preview is where the team talks about the booklet. Write a comment, say which
          section and page it is about, and it appears for the editor in that very section.
        </p>
        <Shot src="/guide/07-comments.jpg" alt="The comments panel next to the preview" />
        <p>
          When a booklet is ready, an editor presses <b>Send for review</b>. A reviewer then either presses <b>Approve</b> or
          sends it back with <b>Ask for changes</b>. Editing a booklet that was already approved or published sends it back
          to <i>Being written</i>, so nothing goes out without being looked at again.
        </p>
      </>
    ),
  },
  {
    id: 'publish',
    title: 'Publishing and sharing',
    body: (
      <>
        <p>
          Under <b>Publish &amp; versions</b>, a reviewer or admin presses <b>Publish</b>. That freezes the booklet as a
          numbered version and renders both PDFs. Work can carry on afterwards; the published version stays as it was.
        </p>
        <Shot src="/guide/08-versions.jpg" alt="Publish & versions — past versions, comparing, restoring and sharing" />
        <p>
          <b>Compare</b> shows what changed between a version and the booklet as it stands now. <b>Restore</b> brings an old
          version's content back.
        </p>
        <p>
          <b>Share</b> creates a link to the PDF that anyone can open without signing in — send it by WhatsApp or email.
          <b> Stop sharing</b> kills the link again.
        </p>
      </>
    ),
  },
  {
    id: 'history',
    title: 'History of a booklet',
    body: (
      <>
        <p>
          <b>History</b> in the top bar shows who changed what in this booklet, with the value before and after. Edits by
          the same person within two minutes are grouped into one line.
        </p>
        <Shot src="/guide/09-history.jpg" alt="History — who changed what in this booklet" />
      </>
    ),
  },
  {
    id: 'activity',
    title: 'Activity log',
    adminOnly: true,
    body: (
      <>
        <p>
          Admins only. Every change anyone makes across the whole app: booklets created, edited and deleted, photos
          added and removed, imports, publishing, share links, users and templates — plus sign-ins and failed sign-ins.
        </p>
        <Shot src="/guide/10-activity.jpg" alt="Activity — everything the team has changed, filtered by person or action" />
        <p>
          Filter by person or by kind of action. Deletions are marked in red, so “who deleted that booklet?” takes one look.
          A booklet's own <b>History</b> disappears with the booklet; this log keeps the line that says it was deleted.
        </p>
      </>
    ),
  },
  {
    id: 'users',
    title: 'Users',
    adminOnly: true,
    body: (
      <>
        <p>
          Admins add people under <b>Users</b>, set a role, reset a password, or disable someone who has left. The last
          admin cannot be removed or demoted, so the app can never be locked out.
        </p>
        <Shot src="/guide/11-users.jpg" alt="Users — who can sign in, and what they may do" />
      </>
    ),
  },
  {
    id: 'templates',
    title: 'Templates',
    adminOnly: true,
    body: (
      <>
        <p>
          A template holds the colours, the fonts and the order of the pages. The built-in WnW template is locked; copy
          it, then edit the copy and watch the sample booklet change as you go.
        </p>
        <Shot src="/guide/12-templates.jpg" alt="Templates — colours, fonts and page order" />
        <p>
          The sketches, stamps and tape strips come from the original PowerPoint artwork and keep their own colours.
          A booklet picks its template on the <b>Photos &amp; pages</b> step.
        </p>
      </>
    ),
  },
  {
    id: 'trouble',
    title: 'When something looks wrong',
    body: (
      <table className="table plain">
        <thead><tr><th>What you see</th><th>What to do</th></tr></thead>
        <tbody>
          <tr>
            <td>“Text too long — shorten it” on a page</td>
            <td>Shorten that text in step 2, or click it in the preview and cut a few words. It blocks approval and publishing.</td>
          </tr>
          <tr>
            <td>The booklet will not publish</td>
            <td>Check <b>Worth a look</b>: an unfilled price or overflowing text is usually the reason.</td>
          </tr>
          <tr>
            <td>A price shows as pending</td>
            <td>It was never filled in. Fill it under <b>Packages &amp; pricing</b>.</td>
          </tr>
          <tr>
            <td>The spreadsheet read nothing</td>
            <td>Pick the right sheet tab and header row, then map the columns by hand and read the sheet again. Or start from the empty Excel form.</td>
          </tr>
          <tr>
            <td>The map is empty</td>
            <td>Add at least two cities under <b>Route</b> in step 2, or upload a map screenshot instead.</td>
          </tr>
          <tr>
            <td>A photo looks cropped</td>
            <td>Move the <b>Focal point</b> sliders, or upload one at the size the slot asks for.</td>
          </tr>
          <tr>
            <td>You were signed out</td>
            <td>Sign in again — the page you were on is kept, and your work was saved as you typed.</td>
          </tr>
        </tbody>
      </table>
    ),
  },
];

export function HelpPage() {
  const { can } = useAuth();
  const sections = SECTIONS.filter((s) => !s.adminOnly || can('admin'));
  const [active, setActive] = useState(sections[0].id);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Highlight the section the reader is looking at.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (seen[0]?.target.id) setActive(seen[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px' },
    );
    bodyRef.current?.querySelectorAll('section[id]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections.length]);

  return (
    <>
      <div className="row" style={{ marginBottom: 18 }}>
        <div>
          <h1 className="title">User guide</h1>
          <div className="sub">How to turn a Word file and a price sheet into a printed booklet.</div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn small ghost" onClick={() => window.print()}>Print this guide</button>
      </div>

      <div className="help-layout">
        <nav className="help-nav">
          {sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} className={active === s.id ? 'active' : ''}>{s.title}</a>
          ))}
        </nav>

        <div className="help-body" ref={bodyRef}>
          {sections.map((s) => (
            <section className="panel help-section" id={s.id} key={s.id}>
              <h2 className="section">{s.title}</h2>
              {s.body}
            </section>
          ))}
        </div>
      </div>
    </>
  );
}
